'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { X, CreditCard, Smartphone, Building2, Loader2, Check, Shield } from 'lucide-react';
import { toast } from 'sonner';

interface DepositModalProps {
  onClose: () => void;
  currency: string;
  symbol: string;
}

interface DepositFormData {
  amount: string;
  method: string;
}

const quickAmounts = [10, 25, 50, 100, 250, 500];

const paymentMethods = [
  { id: 'pm-card', value: 'card', label: 'Credit / Debit Card', icon: CreditCard, sublabel: 'Visa, Mastercard, Amex', fee: '1.5%' },
  { id: 'pm-upi', value: 'upi', label: 'UPI / Net Banking', icon: Smartphone, sublabel: 'Instant transfer', fee: '0%' },
  { id: 'pm-bank', value: 'bank', label: 'Bank Transfer', icon: Building2, sublabel: '1–3 business days', fee: '0%' },
];

export default function DepositModal({ onClose, currency, symbol }: DepositModalProps) {
  const [step, setStep] = useState<'form' | 'loading' | 'success'>('form');
  const [selectedMethod, setSelectedMethod] = useState('card');
  const [quickAmount, setQuickAmount] = useState<number | null>(null);

  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm<DepositFormData>({
    defaultValues: { method: 'card' },
  });

  const amountValue = watch('amount');
  const numAmount = parseFloat(amountValue) || 0;
  const selectedMethodData = paymentMethods.find((m) => m.value === selectedMethod);
  const feePercent = selectedMethodData?.fee === '0%' ? 0 : 0.015;
  const feeAmount = numAmount * feePercent;
  const totalAmount = numAmount + feeAmount;

  const onSubmit = async () => {
    setStep('loading');
    // Backend integration point: POST /api/wallet/deposit
    await new Promise((r) => setTimeout(r, 1800));
    setStep('success');
    toast.success(`${symbol}${numAmount.toFixed(2)} deposited successfully!`);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: '520px' }} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: 'var(--border)' }}>
          <h3 className="text-base font-bold text-foreground">
            {step === 'success' ? 'Deposit Successful!' : 'Add Funds to Wallet'}
          </h3>
          {step !== 'loading' && (
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted transition-colors" aria-label="Close">
              <X size={16} style={{ color: 'var(--muted-foreground)' }} />
            </button>
          )}
        </div>

        <div className="p-5">
          {step === 'form' && (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              {/* Amount */}
              <div>
                <label className="block text-sm font-semibold mb-2 text-foreground" htmlFor="deposit-amount">
                  Deposit Amount ({currency})
                </label>
                <p className="text-xs mb-2" style={{ color: 'var(--muted-foreground)' }}>
                  Minimum: {symbol}10 · Maximum: {symbol}1,000 per transaction
                </p>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold" style={{ color: 'var(--muted-foreground)' }}>
                    {symbol}
                  </span>
                  <input
                    id="deposit-amount"
                    type="number"
                    step="0.01"
                    min="10"
                    max="1000"
                    placeholder="0.00"
                    className="w-full pl-8 pr-4 py-3 text-lg font-bold rounded-xl outline-none transition-all"
                    style={{
                      background: 'var(--input)',
                      border: `1px solid ${errors.amount ? '#F43F5E' : 'var(--border)'}`,
                      color: 'var(--foreground)',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                    onFocus={(e) => { if (!errors.amount) e.target.style.borderColor = 'var(--primary)'; }}
                    onBlur={(e) => { if (!errors.amount) e.target.style.borderColor = 'var(--border)'; }}
                    {...register('amount', {
                      required: 'Please enter an amount',
                      min: { value: 10, message: `Minimum deposit is ${symbol}10` },
                      max: { value: 1000, message: `Maximum deposit is ${symbol}1,000` },
                    })}
                  />
                </div>
                {errors.amount && <p className="text-xs mt-1" style={{ color: '#F43F5E' }}>{errors.amount.message}</p>}

                {/* Quick amounts */}
                <div className="grid grid-cols-6 gap-2 mt-3">
                  {quickAmounts.map((amt) => (
                    <button
                      key={`qa-dep-${amt}`}
                      type="button"
                      onClick={() => { setValue('amount', String(amt)); setQuickAmount(amt); }}
                      className="py-2 rounded-lg text-xs font-semibold transition-all"
                      style={{
                        background: quickAmount === amt ? 'rgba(124,58,237,0.2)' : 'var(--muted)',
                        border: `1px solid ${quickAmount === amt ? 'rgba(124,58,237,0.5)' : 'var(--border)'}`,
                        color: quickAmount === amt ? '#A78BFA' : 'var(--muted-foreground)',
                      }}
                    >
                      {symbol}{amt}
                    </button>
                  ))}
                </div>
              </div>

              {/* Payment method */}
              <div>
                <label className="block text-sm font-semibold mb-2 text-foreground">Payment Method</label>
                <div className="space-y-2">
                  {paymentMethods.map((method) => (
                    <label
                      key={method.id}
                      className="flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all"
                      style={{
                        background: selectedMethod === method.value ? 'rgba(124,58,237,0.1)' : 'var(--muted)',
                        border: `1px solid ${selectedMethod === method.value ? 'rgba(124,58,237,0.4)' : 'var(--border)'}`,
                      }}
                    >
                      <input
                        type="radio"
                        value={method.value}
                        checked={selectedMethod === method.value}
                        onChange={() => setSelectedMethod(method.value)}
                        className="accent-primary"
                      />
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ background: 'var(--card)' }}>
                        <method.icon size={16} style={{ color: selectedMethod === method.value ? '#A78BFA' : 'var(--muted-foreground)' }} />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold" style={{ color: selectedMethod === method.value ? '#A78BFA' : 'var(--foreground)' }}>
                          {method.label}
                        </p>
                        <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{method.sublabel}</p>
                      </div>
                      <span className="text-xs font-bold" style={{ color: method.fee === '0%' ? '#10B981' : '#F59E0B' }}>
                        Fee: {method.fee}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Fee summary */}
              {numAmount > 0 && (
                <div className="p-4 rounded-xl space-y-2" style={{ background: 'var(--muted)' }}>
                  <div className="flex justify-between text-xs">
                    <span style={{ color: 'var(--muted-foreground)' }}>Deposit amount</span>
                    <span className="font-semibold text-foreground" style={{ fontVariantNumeric: 'tabular-nums' }}>{symbol}{numAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span style={{ color: 'var(--muted-foreground)' }}>Processing fee ({selectedMethodData?.fee})</span>
                    <span className="font-semibold" style={{ color: feeAmount > 0 ? '#F59E0B' : '#10B981', fontVariantNumeric: 'tabular-nums' }}>
                      {feeAmount > 0 ? `+${symbol}${feeAmount.toFixed(2)}` : 'Free'}
                    </span>
                  </div>
                  <div className="h-px" style={{ background: 'var(--border)' }} />
                  <div className="flex justify-between text-sm">
                    <span className="font-bold text-foreground">Total charged</span>
                    <span className="font-extrabold" style={{ color: '#A78BFA', fontVariantNumeric: 'tabular-nums' }}>{symbol}{totalAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span style={{ color: 'var(--muted-foreground)' }}>Added to wallet</span>
                    <span className="font-bold" style={{ color: '#10B981', fontVariantNumeric: 'tabular-nums' }}>{symbol}{numAmount.toFixed(2)}</span>
                  </div>
                </div>
              )}

              {/* Compliance note */}
              <div className="flex items-start gap-2 p-3 rounded-xl"
                style={{ background: 'rgba(6,182,212,0.06)', border: '1px solid rgba(6,182,212,0.2)' }}>
                <Shield size={13} style={{ color: '#06B6D4', flexShrink: 0, marginTop: '1px' }} />
                <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                  Deposits are processed securely. Funds are available immediately for free activities. Paid competition access requires KYC verification. Daily deposit limit: {symbol}1,000.
                </p>
              </div>

              <button
                type="submit"
                className="btn-primary w-full justify-center py-3 text-base"
              >
                <CreditCard size={16} />
                Deposit {numAmount > 0 ? `${symbol}${numAmount.toFixed(2)}` : 'Funds'}
              </button>
            </form>
          )}

          {step === 'loading' && (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <Loader2 size={36} className="animate-spin" style={{ color: '#A78BFA' }} />
              <div className="text-center">
                <p className="text-sm font-semibold text-foreground">Processing deposit...</p>
                <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>Connecting to payment provider</p>
              </div>
            </div>
          )}

          {step === 'success' && (
            <div className="flex flex-col items-center justify-center py-8 gap-4 text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(16,185,129,0.2)', border: '2px solid #10B981' }}>
                <Check size={28} style={{ color: '#10B981' }} />
              </div>
              <div>
                <p className="text-2xl font-extrabold text-foreground mb-1" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {symbol}{numAmount.toFixed(2)} Added!
                </p>
                <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                  Your wallet balance has been updated. Ready to compete!
                </p>
              </div>
              <div className="w-full p-3 rounded-xl" style={{ background: 'var(--muted)' }}>
                <div className="flex justify-between text-xs">
                  <span style={{ color: 'var(--muted-foreground)' }}>New available balance</span>
                  <span className="font-extrabold" style={{ color: '#10B981', fontVariantNumeric: 'tabular-nums' }}>
                    {symbol}{(124.50 + numAmount).toFixed(2)}
                  </span>
                </div>
              </div>
              <button onClick={onClose} className="btn-primary w-full justify-center py-2.5">
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}